import type { Layout } from "plotly.js";
import type { PlotParams } from "react-plotly.js";

type PlotlyAxis = {
  title?: string | { text?: string; standoff?: number; [k: string]: unknown };
  automargin?: boolean;
  ticklabelposition?:
    | "outside"
    | "inside"
    | "outside top"
    | "inside top"
    | "outside left"
    | "inside left"
    | "outside right"
    | "inside right"
    | "outside bottom"
    | "inside bottom";
  ticks?: "outside" | "inside" | "";
  ticklen?: number;
  [k: string]: unknown;
};

// Internal layout shape used only for defaulting logic
type PlotlyInternalLayout = {
  autosize?: boolean;
  margin?: { t?: number; r?: number; b?: number; l?: number };
  legend?: { orientation?: string; y?: number };
  height?: number;
  title?: string | { text?: string; pad?: { t?: number; b?: number }; [k: string]: unknown };
  xaxis?: PlotlyAxis;
  yaxis?: PlotlyAxis;
  yaxis2?: PlotlyAxis;
  [key: string]: unknown;
};

function numberOrDefault(value: unknown, def: number): number {
  const n = typeof value === "number" ? value : Number(value);
  return Number.isFinite(n) ? n : def;
}

export function applyPlotlyDefaults(obj: PlotParams): PlotParams {
  if (!obj || typeof obj !== "object") return obj;
  const out: PlotParams = { ...obj };
  const layout: PlotlyInternalLayout = { ...((out.layout as object) || {}) };
  layout.autosize = true;
  layout.margin = {
    t: 80,
    r: 110,
    b: 120,
    l: 90,
    ...(layout.margin || {}),
  };
  layout.legend = {
    orientation: "h",
    y: -0.3,
    ...(layout.legend || {}),
  };
  layout.height = layout.height || 420;

  // Title padding to avoid overlap with top edge
  const existingTitle = layout.title;
  if (typeof existingTitle === "string") {
    layout.title = { text: existingTitle, pad: { t: 12, b: 6 } } as Layout["title"];
  } else {
    const titleObj = (existingTitle as Record<string, unknown>) || {};
    const pad = { t: 12, b: 6, ...((titleObj["pad"] as Record<string, unknown>) || {}) };
    layout.title = { ...(titleObj as object), pad } as Layout["title"];
  }

  // Axis defaults: give axis titles breathing room and prevent clipping
  const xaxis: PlotlyAxis = { ...(layout.xaxis || {}) };
  const xTitleObj =
    typeof xaxis.title === "string"
      ? { text: xaxis.title }
      : (xaxis.title as Record<string, unknown>) || {};
  xaxis.title = {
    ...xTitleObj,
    standoff: Math.max(12, numberOrDefault((xTitleObj as Record<string, unknown>).standoff, 0)),
  };
  xaxis.automargin = true;
  xaxis.ticklabelposition = xaxis.ticklabelposition || "outside";
  xaxis.ticks = xaxis.ticks || "outside";
  xaxis.ticklen = xaxis.ticklen || 6;
  layout.xaxis = xaxis;

  const yaxis: PlotlyAxis = { ...(layout.yaxis || {}) };
  const yTitleObj =
    typeof yaxis.title === "string"
      ? { text: yaxis.title }
      : (yaxis.title as Record<string, unknown>) || {};
  yaxis.title = {
    ...yTitleObj,
    standoff: Math.max(40, numberOrDefault((yTitleObj as Record<string, unknown>).standoff, 0)),
  };
  yaxis.automargin = true;
  yaxis.ticklabelposition = yaxis.ticklabelposition || "outside";
  yaxis.ticks = yaxis.ticks || "outside";
  yaxis.ticklen = yaxis.ticklen || 6;
  layout.yaxis = yaxis;

  const yaxis2: PlotlyAxis = { ...(layout.yaxis2 || {}) };
  if (Object.keys(yaxis2).length > 0) {
    const y2TitleObj =
      typeof yaxis2.title === "string"
        ? { text: yaxis2.title }
        : (yaxis2.title as Record<string, unknown>) || {};
    yaxis2.title = {
      ...y2TitleObj,
      standoff: Math.max(40, numberOrDefault((y2TitleObj as Record<string, unknown>).standoff, 0)),
    };
    yaxis2.automargin = true;
    yaxis2.ticklabelposition = yaxis2.ticklabelposition || "outside";
    yaxis2.ticks = yaxis2.ticks || "outside";
    yaxis2.ticklen = yaxis2.ticklen || 6;
    layout.yaxis2 = yaxis2;
    if (layout.margin) layout.margin.r = Math.max(layout.margin.r ?? 0, 110);
  }

  out.layout = layout as Partial<Layout>;
  // The Plotly logo and edit-chart modebar entries link out to plotly.com;
  // force them off so charts never expose external links.
  out.config = {
    responsive: true,
    ...(out.config || {}),
    displaylogo: false,
    showLink: false,
  };
  // Ensure data outside labels aren't clipped
  if (Array.isArray(out.data)) {
    out.data = out.data.map(d => ({ ...d, cliponaxis: false }));
  }
  return out;
}
